// 사장님 첫 화면 — 기사가 현장에서 「문제 있음」으로 알린 집(우유 부족·출입 불가·집 못 찾음·기타). 그 집은 미배송으로 남아 있고 보호자 문자는 안 갔다(대표 결정 Q9)
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { doc, onSnapshot, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { orgQuery } from "@/lib/org";
import { useAuth } from "@/store/auth";
import { fmtDateTime } from "@/lib/format";
import { plainError } from "@/lib/plainError";

interface Issue {
  id: string;
  students?: Array<{ id: string; name: string; school: string }>;
  place?: string;
  reasonLabel?: string;
  memo?: string;
  driverName?: string;
  createdAt?: { toDate?: () => Date };
}

export default function DeliveryIssues() {
  const { user } = useAuth();
  const [rows, setRows] = useState<Issue[]>([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    const unsub = onSnapshot(
      orgQuery("deliveryIssues", where("resolved", "==", false)),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Issue, "id">) }));
        list.sort((a, b) => (b.createdAt?.toDate?.()?.getTime() ?? 0) - (a.createdAt?.toDate?.()?.getTime() ?? 0));
        setRows(list);
      },
      () => setRows([]),
    );
    return () => unsub();
  }, []);

  const resolve = async (id: string) => {
    setErr("");
    try {
      await updateDoc(doc(db, "deliveryIssues", id), { resolved: true, resolvedAt: serverTimestamp(), resolvedBy: user?.email || user?.uid || "" });
    } catch (e) {
      setErr(`처리 표시를 못 했습니다 — ${plainError(e)}`);
    }
  };

  if (rows.length === 0) return null;
  return (
    <div className="card p-4 border-2 border-rose-300 bg-rose-50">
      <div className="font-bold text-rose-800">🚧 기사님이 알린 현장 문제 {rows.length}곳</div>
      <p className="text-sm text-rose-700 mt-1">이 집들은 아직 <b>미배송</b>이고 보호자에게 문자는 가지 않았습니다. 확인하고 처리한 뒤 「처리함」을 눌러 주세요.</p>
      <ul className="mt-2 text-sm divide-y divide-rose-200">
        {rows.map((r) => {
          const who = (r.students || []).map((s) => `${s.school} ${s.name}`).join(", ");
          const firstName = r.students?.[0]?.name || "";
          return (
            <li key={r.id} className="py-2 space-y-1">
              <div><b>{r.reasonLabel}</b> — {who}{r.place ? ` · ${r.place}` : ""}</div>
              {r.memo && <div className="text-rose-900 break-keep">“{r.memo}”</div>}
              <div className="text-xs text-slate-600">기사 {r.driverName || "—"} · {r.createdAt?.toDate ? fmtDateTime(r.createdAt.toDate()) : ""}</div>
              <div className="flex gap-3 flex-wrap">
                {firstName && <Link className="underline text-rose-800 min-h-[36px] leading-[36px]" to={`/admin/students?q=${encodeURIComponent(firstName)}`}>학생 보기</Link>}
                <button className="underline text-rose-800 min-h-[36px]" onClick={() => resolve(r.id)}>처리함</button>
              </div>
            </li>
          );
        })}
      </ul>
      {err && <div className="text-sm text-rose-800 mt-1" role="alert">{err}</div>}
    </div>
  );
}
